import { useState } from "react"

import { LogoIcon } from "@/components/LogoIcon"
import TalentStats from "@/components/TalentStats"
import { Overline } from "@/components/typography"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import type { Platform, Talent } from "@/shared/types"
import { abbreviateNumber } from "@/utils/formatNumbers"

export type PlatformCellProps = {
  platform: Platform
  talent?: Talent
}

/**
 * One table column per platform. Figma `Pitch - Followers with View`: 32px
 * linked mark, stacked uppercase count + label, tiny View Full Stats link.
 */
export function PlatformCell({ platform, talent }: PlatformCellProps) {
  const [statsOpen, setStatsOpen] = useState(false)
  const username = talent?.platformData?.[platform]?.username
  const followers = talent?.platformData?.[platform]?.followers

  if (!username) return null

  const label = platform === "YouTube" ? "Subscribers" : "Followers"
  const title = talent?.name || `@${username}`

  return (
    <div className="min-w-28">
      {/* 32px mark + 8px + stacked overlines (Figma 70:4111). gap-2, not
          icon margin — table columns shrink on mobile and ate the space. */}
      <div className="flex items-start gap-2">
        <a
          href={profileHref(platform, username)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0"
        >
          <LogoIcon brand={platform} className="size-8" />
        </a>
        {followers != null ? (
          <div className="shrink-0">
            <Overline className="font-medium">
              {abbreviateNumber(followers)}
            </Overline>
            <Overline className="font-medium">{label}</Overline>
          </div>
        ) : null}
      </div>
      {talent ? (
        <>
          <Button
            type="button"
            variant="link"
            size="xs"
            className="mt-1 h-auto p-0 font-normal uppercase"
            onClick={() => setStatsOpen(true)}
          >
            <span className="text-tiny/3">View Full Stats</span>
          </Button>
          <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
              <DialogTitle className="sr-only">Stats for {title}</DialogTitle>
              <TalentStats tal={talent} />
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </div>
  )
}

export function PlatformCellSkeleton() {
  return (
    <div className="min-w-28">
      <div className="flex items-start gap-2">
        <Skeleton className="size-8 shrink-0 rounded-md" />
        <div className="space-y-1">
          <Skeleton className="h-3 w-10" />
          <Skeleton className="h-3 w-14" />
        </div>
      </div>
      <Skeleton className="mt-1 h-3 w-20" />
    </div>
  )
}

function profileHref(platform: Platform, username: string) {
  if (platform === "TikTok") return `https://tiktok.com/@${username.toLowerCase()}`
  if (platform === "Instagram") return `https://instagram.com/${username}`
  return `https://www.youtube.com/@${username}`
}
